/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/dashboards/ChairmanDashboard.tsx
 * PURPOSE: Owner landing page (TENANT_OWNER home_route). No backend serves an
 *          owner KPI feed, so the page shows an honest empty state and links to
 *          the owner reports; it never renders invented figures or staff names
 *          (audit A1, 2026-10-05).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * COMPLIANCE: SDLC 6.5.0 + Zero Mock Tolerance + Vietnamese F&B Design System
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance) + P6 (Component Library)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * CTO MANDATE: 100% design token usage, Vietnamese messaging, mobile-first
 *
 * @business-context
 * - Domain: Vietnamese F&B Operations, Executive Dashboard
 * - Impact: Business Intelligence, Decision Making, Real-time Monitoring
 * - Stakeholders: Chairman, Executive Team, Management
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Dashboard
 * - Pattern: React Component, Design System Tokens, Responsive
 * - Dependencies: React 18, TypeScript, Design System CSS
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 *  */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/auth";

// Vietnamese messaging constants - theo design spec
const VI_MESSAGES = {
  title: "Bảng Điều Khiển Chủ Tịch",
  welcome: "Xin chào",
  noData: "Chưa có dữ liệu",
  // The KPI feed this page used to call (/api/v1/dashboard/executive) has no
  // backend route; the figures it rendered were invented. Say so plainly.
  noDataReason:
    "Bảng điều khiển tổng hợp chưa được kết nối với nguồn dữ liệu thật của doanh nghiệp, " +
    "nên chưa có số liệu nhân sự, ca làm, nhiệm vụ hay doanh thu để hiển thị. " +
    "Vui lòng xem các báo cáo bên dưới.",
  quickActions: "Thao Tác Nhanh",
  laborCost: "Chi Phí Nhân Công",
  bhxhCompliance: "BHXH Compliance",
  boardReport: "Báo Cáo Tài Chính",
  logout: "Đăng Xuất",
};

const ChairmanDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Same as ManagerLayout/TenantAdminLayout: the auth store owns the session.
  // localStorage.clear() wiped unrelated keys and left the in-memory store
  // authenticated until reload.
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="nqh-dashboard">
      {/* Navigation */}
      <nav className="nqh-nav" style={{ marginBottom: "var(--space-6)", borderRadius: "var(--radius-xl)" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 var(--space-4)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "64px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  height: "40px",
                  width: "40px",
                  background: "linear-gradient(135deg, var(--primary-500) 0%, var(--primary-700) 100%)",
                  borderRadius: "var(--radius-lg)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "var(--space-3)",
                }}
              >
                <span style={{ color: "white", fontWeight: "bold", fontSize: "var(--text-sm)" }}>NQH</span>
              </div>
              <h1 style={{ fontSize: "var(--text-xl)", fontWeight: "var(--weight-bold)", color: "var(--gray-900)", margin: 0 }}>
                {VI_MESSAGES.title}
              </h1>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-700)", fontWeight: "var(--weight-medium)" }}>
                {VI_MESSAGES.welcome}, {user?.full_name || "Chủ tịch"}
              </span>
              <button
                onClick={handleLogout}
                style={{
                  fontSize: "var(--text-sm)",
                  background: "var(--error)",
                  color: "white",
                  padding: "var(--space-2) var(--space-4)",
                  borderRadius: "var(--radius-lg)",
                  border: "none",
                  cursor: "pointer",
                  transition: "all var(--duration-200) var(--ease-out)",
                }}
              >
                {VI_MESSAGES.logout}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="nqh-dashboard-header">
        <span style={{ fontSize: "2rem" }}>📊</span>
        <h1 className="nqh-dashboard-title">{VI_MESSAGES.title}</h1>
      </div>

      {/* Honest empty state: no backend serves owner KPIs, alerts or a live staff list */}
      <div className="nqh-card" role="status">
        <h2 style={{ margin: "0 0 var(--space-2) 0", fontSize: "var(--text-xl)", fontWeight: "var(--weight-bold)", color: "var(--gray-900)" }}>
          {VI_MESSAGES.noData}
        </h2>
        <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--gray-700)" }}>
          {VI_MESSAGES.noDataReason}
        </p>
      </div>

      {/* Quick Actions */}
      <div className="nqh-card" style={{ marginTop: "var(--space-6)" }}>
        <h2 style={{ margin: "0 0 var(--space-4) 0", fontSize: "var(--text-xl)", fontWeight: "var(--weight-bold)", color: "var(--gray-900)" }}>
          🚀 {VI_MESSAGES.quickActions}
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--space-4)" }}>
          <button
            onClick={() => navigate("/cfo/labor-cost")}
            className="nqh-dashboard-card"
            style={{ cursor: "pointer", border: "none", textAlign: "left", background: "linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%)", color: "white" }}
          >
            <span style={{ display: "block", fontSize: "2rem", marginBottom: "var(--space-3)" }}>💼</span>
            <span style={{ fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)" }}>{VI_MESSAGES.laborCost}</span>
            <p style={{ fontSize: "var(--text-sm)", opacity: 0.9, marginTop: "var(--space-1)" }}>CFO Labor Cost Dashboard</p>
          </button>
          <button
            onClick={() => navigate("/hr/bhxh-compliance")}
            className="nqh-dashboard-card"
            style={{ cursor: "pointer", border: "none", textAlign: "left", background: "linear-gradient(135deg, var(--secondary-500) 0%, var(--secondary-600) 100%)", color: "white" }}
          >
            <span style={{ display: "block", fontSize: "2rem", marginBottom: "var(--space-3)" }}>🏥</span>
            <span style={{ fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)" }}>{VI_MESSAGES.bhxhCompliance}</span>
            <p style={{ fontSize: "var(--text-sm)", opacity: 0.9, marginTop: "var(--space-1)" }}>Bảo hiểm xã hội tự động</p>
          </button>
          <button
            onClick={() => navigate("/board/consolidated-financials")}
            className="nqh-dashboard-card"
            style={{ cursor: "pointer", border: "none", textAlign: "left", background: "linear-gradient(135deg, var(--info) 0%, #1d4ed8 100%)", color: "white" }}
          >
            <span style={{ display: "block", fontSize: "2rem", marginBottom: "var(--space-3)" }}>📊</span>
            <span style={{ fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)" }}>{VI_MESSAGES.boardReport}</span>
            <p style={{ fontSize: "var(--text-sm)", opacity: 0.9, marginTop: "var(--space-1)" }}>Board Consolidated Report</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChairmanDashboard;
